import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Lock, ArrowRight, Loader2, KeyRound, Eye, EyeOff, Phone, MessageSquare, ShieldCheck, QrCode } from 'lucide-react';
import axios from 'axios';
import { api } from '@/services/api';
import { supabase } from '@/lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://scalemicrofin.scalebot.in';

function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // 2FA State
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Load saved credentials on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    if (savedEmail && savedPassword) {
      setFormData({
        email: savedEmail,
        password: savedPassword,
      });
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      // Regular login
      const data = await login(formData.email, formData.password);

      // Check for 2FA requirements
      if (data.status === 'setup_required') {
        setTempToken(data.temp_token);
        try {
          const setupData = await api.auth.setup2FA(data.temp_token);
          setQrCode(setupData.qr_code);
          setSecret(setupData.secret);
          setShow2FASetup(true);
        } catch (setupError) {
          console.error(setupError);
          toast.error("Failed to initiate 2FA setup");
        }
        setLoading(false);
        return;
      } else if (data.status === '2fa_required') {
        setTempToken(data.temp_token);
        setShow2FAVerify(true);
        setLoading(false);
        return;
      }

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
        localStorage.setItem('rememberedPassword', formData.password);
      } else {
        // Clear saved credentials if remember me is unchecked
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }

      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Login failed');
    } finally {
      // Only set loading false if we are not showing 2FA screens
      if (!show2FASetup && !show2FAVerify) {
        setLoading(false);
      }
    }
  };

  const handle2FAVerifySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api.auth.verify2FA({ code: otpCode }, tempToken);

      // Save token and redirect
      localStorage.setItem('token', data.token);

      toast.success('Login successful!');
      // Force reload to update auth state
      window.location.href = '/';

    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP code");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call the backend forgot password API
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });

      toast.success(response.data.message || 'Password reset link sent! Please check your email.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      // Show the actual error message from backend
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset link. Please try again.';

      if (error.response?.status === 404) {
        // User not found
        toast.error(errorMessage, {
          duration: 6000, // Show for 6 seconds
        });
      } else {
        // Other errors
        toast.error(errorMessage, {
          duration: 5000,
        });
      }

      // Don't close the form on error so user can try again
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left side - Branding */}
        <div className="space-y-8 fade-in">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_scalemicrofin/artifacts/zwx3wriv_scaleMicroFin-icon%27png.png"
                alt="JD Group Logo"
                className="h-20 w-20 drop-shadow-lg"
              />
              <div>
                <h1 className="text-5xl sm:text-6xl font-bold gradient-text leading-tight">
                  JD Group
                </h1>
                <p className="text-sm text-emerald-600 font-medium mt-1">Trusted Microfinance Platform</p>
              </div>
            </div>
          </div>

          {/* Hide on mobile - text and feature list */}
          <p className="hidden md:block text-lg text-gray-700 max-w-md leading-relaxed">
            Empowering local communities through <span className="font-semibold text-emerald-600">transparent</span> and <span className="font-semibold text-emerald-600">trustworthy</span> microfinance management.
          </p>

          <div className="hidden md:block space-y-4">
            <div className="flex items-start gap-3 slide-in-left">
              <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Secure Member Management</h3>
                <p className="text-sm text-gray-600">Protected KYC and document storage</p>
              </div>
            </div>

            <div className="flex items-start gap-3 slide-in-left" style={{ animationDelay: '0.1s' }}>
              <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Transparent Loan Tracking</h3>
                <p className="text-sm text-gray-600">Real-time EMI and payment monitoring</p>
              </div>
            </div>

            <div className="flex items-start gap-3 slide-in-left" style={{ animationDelay: '0.2s' }}>
              <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Easy Savings Management</h3>
                <p className="text-sm text-gray-600">Automated Lavajam deposits and profit sharing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <Card className="glass card-hover shadow-strong border-0" data-testid="login-card">
          <CardHeader className="space-y-2 pb-6">
            <CardTitle className="text-3xl font-bold gradient-text" data-testid="login-title">
              {showForgotPassword ? 'Reset Password' : 'Login'}
            </CardTitle>
            <CardDescription className="text-base">
              {showForgotPassword
                ? 'Enter your email or phone to receive a password reset link'
                : 'Enter your credentials to access your account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {show2FASetup ? (
              <div className="space-y-6 fade-in">
                <div className="text-center space-y-4">
                  <div className="bg-emerald-50 p-4 rounded-full inline-flex items-center justify-center">
                    <QrCode className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Set up 2FA</h3>
                  <p className="text-sm text-gray-600 max-w-xs mx-auto">
                    Scan this QR code with Google Authenticator to secure your account.
                  </p>
                </div>

                <div className="flex justify-center p-4 bg-white border-2 border-dashed border-gray-200 rounded-xl">
                  <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48 object-contain" />
                </div>

                <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide font-semibold">Setup Key</p>
                  <code className="text-sm font-mono font-bold text-emerald-700 tracking-wider select-all">{secret}</code>
                </div>

                <form onSubmit={handle2FAVerifySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp-setup" className="text-sm font-semibold text-gray-700">
                      Enter 6-digit Code
                    </Label>
                    <Input
                      id="otp-setup"
                      type="text"
                      placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 border-2 focus:border-emerald-500 transition-colors"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-lg"
                    disabled={loading || otpCode.length !== 6}
                  >
                    {loading ? <Loader2 className="animate-spin" /> : 'Verify & Enable 2FA'}
                  </Button>
                </form>
              </div>
            ) : show2FAVerify ? (
              <div className="space-y-6 fade-in">
                <div className="text-center space-y-4">
                  <div className="bg-emerald-50 p-4 rounded-full inline-flex items-center justify-center">
                    <ShieldCheck className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-600">
                    Enter the 6-digit code from your authenticator app.
                  </p>
                </div>

                <form onSubmit={handle2FAVerifySubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="otp-verify" className="text-sm font-semibold text-gray-700">
                      Authentication Code
                    </Label>
                    <Input
                      id="otp-verify"
                      type="text"
                      placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 border-2 focus:border-emerald-500 transition-colors"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      required
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-lg"
                    disabled={loading || otpCode.length !== 6}
                  >
                    {loading ? <Loader2 className="animate-spin" /> : 'Verify & Login'}
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShow2FAVerify(false);
                        setOtpCode('');
                        setLoading(false);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                    >
                      ← Back to Login
                    </button>
                  </div>
                </form>
              </div>
            ) : showForgotPassword ? (
              // Forgot Password Form
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-sm font-semibold text-gray-700">
                    Email or Phone Number
                  </Label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                      <Mail className="text-gray-400" size={20} />
                    </div>
                    <Input
                      id="forgot-email"
                      type="text"
                      placeholder="your@email.com or 9876543210"
                      className="pl-12 h-12 form-input text-base"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> If you don't receive an email or your account is not found, please contact JD Group Admin for assistance with password reset.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-base shadow-lg hover:shadow-xl"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                  >
                    ← Back to Login
                  </button>
                </div>
              </form>
            ) : (
              // Login Form
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                    Email or Phone Number
                  </Label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                      <Mail className="text-gray-400" size={20} />
                    </div>
                    <Input
                      id="email"
                      type="text"
                      placeholder="your@email.com or 9876543210"
                      className="pl-12 h-12 form-input text-base"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                      Password
                    </Label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                        <Lock className="text-gray-400" size={20} />
                      </div>
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="pl-12 pr-14 h-12 form-input text-base"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="password-input"
                      />
                      <div className="absolute right-3 top-1/2 z-20" style={{ transform: 'translateY(-50%)' }}>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-emerald-600 focus:outline-none"
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff size={20} strokeWidth={2} className="flex-shrink-0" />
                          ) : (
                            <Eye size={20} strokeWidth={2} className="flex-shrink-0" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={setRememberMe}
                        className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                      />
                      <Label htmlFor="remember-me" className="text-sm font-medium cursor-pointer text-gray-700">
                        Remember me
                      </Label>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-base shadow-lg hover:shadow-xl"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      Login
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Login;
